<template>
  <div class="column is-full">
    <div class="columns is-multiline">
      <div class="column is-full" :class="recurrenceClass">
        <!-- Criteria selection dropdown - always enabled regardless of other selections -->
        <DropdownMultipleSelect
          label="Criteria"
          placeholder="Select Criteria"
          v-model="cycleData.criteriaIds"
          :options="transformProjectCriteria"
          :multi="true"
          :showSearch="true"
          :isRequired="true"
          :isCreateProject="isCreateProject"
        />
      </div>

      <div class="column is-full" :class="recurrenceClass">
        <!-- Sites selection dropdown - drives the available data collectors -->
        <DropdownMultipleSelect
          label="Sites"
          placeholder="Select Sites"
          v-model="cycleData.siteIds"
          :options="transformProjectSites"
          :multi="true"
          :showSearch="false"
          :isRequired="true"
          :isCreateProject="isCreateProject"
        />
      </div>

      <div class="column is-full" :class="recurrenceClass">
        <!-- Data collectors dropdown with dynamic disabling based on site selection -->
        <DropdownMultipleSelect
          label="Data Collectors"
          placeholder="Select Data Collectors"
          v-model="cycleData.participantEmails"
          :options="transformProjectDataCollectors"
          :multi="true"
          :showSearch="true"
          :disabledOptions="dataCollectorsForNonSelectedSites"
          :disabled="!cycleData.siteIds.length"
          disableDataInfoMsg="This data collector is not added into the selected site(s)."
          :isCreateProject="isCreateProject"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { cloneData } from '@/utils/general.util';
import { RecurrenceType } from '@/jbi-shared/types/project.types';
import DropdownMultipleSelect from '@/components/DropdownMultipleSelect.vue';

/**
 * AuditCycleDataSetup component manages the selection of criteria, sites, and data collectors
 * for cycle data configuration. It implements a cascading selection logic where:
 *
 * 1. Site selection determines which data collectors are available/disabled
 * 2. Collectors are auto-filtered when sites change to maintain valid selections
 * 3. All selection changes are emitted to parent components via a unified change event
 */
@Component({
  name: 'AuditCycleDataSetup',
  components: {
    DropdownMultipleSelect,
  },
})
export default class AuditCycleDataSetup extends Vue {
  @Prop({ type: Array, default: () => [] })
  public projectCriteria!: Array<{
    title: string;
    uniqueCriteriaId: string | number;
  }>;
  @Prop({ type: Array, default: () => [] })
  public projectSites!: Array<{ id: number; name: string }>;
  @Prop({ type: Array, default: () => [] })
  public projectDataCollectors!: Array<{
    email: string;
    siteIds: number[];
  }>;
  @Prop({ type: Array, default: () => [] })
  public selectedCriteria!: Array<string | number>;
  @Prop({ type: Array, default: () => [] })
  public selectedSites!: number[];
  @Prop({ type: Array, default: () => [] })
  public selectedDataCollectors!: string[];
  @Prop({ type: String, required: true })
  public recurrence!: RecurrenceType;
  @Prop({ type: Boolean, required: false })
  public isCreateProject!: Boolean;

  public cycleData = {
    criteriaIds: [] as string[],
    siteIds: [] as number[],
    participantEmails: [] as string[],
  };

  public mounted() {
    this.cycleData.criteriaIds = cloneData(this.selectedCriteria);
    this.cycleData.siteIds = cloneData(this.selectedSites);
    this.cycleData.participantEmails = cloneData(this.selectedDataCollectors);
  }

  get showLabel(): boolean {
    return (
      this.recurrence === RecurrenceType.ONE_OFF ||
      this.recurrence === RecurrenceType.ONGOING
    );
  }

  /**
   * Returns a list of data collectors whose assigned siteIds intersect with
   * the currently selected siteIds for the audit cycle.
   * Each returned collector includes only the email and siteIds fields.
   */
  get dataCollectorsForSelectedSites(): Array<{
    email: string;
    siteIds: number[];
  }> {
    return this.projectDataCollectors.filter((dataCollector) => {
      return dataCollector.siteIds.some((site) =>
        this.cycleData.siteIds.includes(site),
      );
    });
  }

  /**
   * Returns the emails of data collectors who are not assigned
   * to the currently selected audit cycle (i.e., their emails are not in participantEmails).
   */
  get dataCollectorsForNonSelectedSites(): string[] {
    const validEmails = this.dataCollectorsForSelectedSites.map((e) => e.email);
    return this.projectDataCollectors
      .filter((dataCollector) => !validEmails.includes(dataCollector.email))
      .map((dataCollector) => dataCollector.email);
  }

  get transformProjectCriteria() {
    const data = this.projectCriteria.map((pc) => {
      return { id: pc.uniqueCriteriaId, value: pc.title };
    });
    return data;
  }

  get transformProjectSites() {
    return this.projectSites.map((ps) => {
      return { id: ps.id, value: ps.name };
    });
  }

  get transformProjectDataCollectors() {
    return this.projectDataCollectors.map((dc) => {
      return { id: dc.email, value: dc.email };
    });
  }

  get recurrenceClass() {
    if (
      this.recurrence === RecurrenceType.ONE_OFF ||
      this.recurrence === RecurrenceType.ONGOING
    ) {
      return 'recurrence-highlight';
    }
    return '';
  }

  @Watch('cycleData.criteriaIds', { deep: true })
  public onCriteriaUpdates() {
    this.emitChange();
  }

  @Watch('cycleData.siteIds', { deep: true })
  public onSiteUpdates() {
    /**
     * Filters data collectors based on the currently selected audit site(s), and limits their associated site access to match the selected audit scope.
     *
     * - Checks if a collector's email exists in the current cycle's participantEmails.
     * - Filters each collector's siteIds to include only those selected in the current cycle.
     * - If the collector has at least one matching site, adds them to the auditParticipants list with filtered siteIds.
     * - Updates cycleData.participantEmails to reflect valid participant emails.
     */
    const auditParticipants: Array<{ email: string; siteIds: number[] }> = [];
    this.projectDataCollectors.forEach((dataCollector) => {
      const sites: number[] = dataCollector.siteIds.filter((sId) =>
        this.cycleData.siteIds.includes(sId),
      );

      if (sites.length > 0) {
        auditParticipants.push({
          ...dataCollector,
          siteIds: sites,
        });
      }
    });
    this.cycleData.participantEmails = auditParticipants.map((ap) => ap.email);
    this.emitChange();
  }

  @Watch('cycleData.participantEmails', { deep: true })
  public onParticipantUpdates() {
    this.emitChange();
  }

  public emitChange() {
    const auditParticipants: {
      email: string;
      siteIds: number[];
    }[] = [];

    this.dataCollectorsForSelectedSites.forEach((dataCollector) => {
      if (this.cycleData.participantEmails.includes(dataCollector.email)) {
        /**
         * Filter the data collector's site IDs
         * to include only those that are part of the selected audit sites
         */
        const sites = dataCollector.siteIds.filter((id) =>
          this.cycleData.siteIds.includes(id),
        );
        /** Add the data collector to the auditParticipants list with filtered site IDs */
        auditParticipants.push({ email: dataCollector.email, siteIds: sites });
      }
    });

    this.$emit('update', {
      auditCriteriaIds: this.cycleData.criteriaIds,
      auditSiteIds: this.cycleData.siteIds,
      auditParticipants,
    });
  }
}
</script>

<style lang="scss" scoped>
.columns.is-multiline {
  margin-bottom: 0;
}

.column {
  padding: 0.75rem !important;
}

/* Apply consistent spacing between dropdown sections */
.column.is-full {
  // margin-bottom: 0.75rem;
}

label {
  font-weight: $weight-bold;
  font-size: 1.2rem;
  color: $dark;
}

.recurrence-highlight {
  padding: 0.75rem 0 !important;
}
</style>
